import {api, RequestParam} from "@/shared/api";

import {DownloadStatus} from "@/shared/ui/downloadStatus";
import {WorkingTimeDateSpanDialog} from "@/features/workingtime/workingTimeDateSpanDialog";
import {RecordingReport, RecordingReportConfig} from "./recordingReport";
import {SetNoteDialog} from "@/features/reports/setNoteDialog";
import {WorkingTimeRequestDialog} from "@/features/requests/workingTimeRequestDialog";
import {WorkingTimeTable} from "@/features/workingtime/workingTimeTable";
import {showRecordingBulkResult} from "@/shared/bulkOperationHelper";
import {Combobox} from "@/shared/ui/combobox";
import {WorkingTimeDialog} from "@/features/workingtime/workingTimeDialog";
import {WorkingTimeDetailsDialog} from "@/features/workingtime/workingTimeDetailsDialog";
import {getWorkingTimeTypesForAddAbsence, getWorkingTimeTypesForAddWorkingTime} from "@/shared/securityHelper";
import {RecordingTable} from "@/shared/recording/recordingTable";
import * as RecordingValidationUtil from "@/shared/validations/recordingValidationUtil";
import {buildValidationsListItem} from "@/shared/validations/validationsListComponent";
import * as Constants from "@/shared/constants";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {DOMUtil} from "@/shared/utils/dom";
import {SecurityStatus} from "@/shared/securityStatus";
import {ReportWorkingTime, User, WorkingTimeBulkOperationResult, WorkingTimeType} from "@/shared/timr";
import {formatDuration, getISODate, parseISODate} from "@/shared/utils/datefunctions";
import {ExportType, WorkingTimeTypeCategory} from "@/shared/timrTypes";
import {WorkingTimeReportingQueryParams} from "@/shared/queryParams";

export interface WorkingTimeReportConfig extends RecordingReportConfig {
	workingTimeTypes: WorkingTimeType[];
	isGroupLeaderForThemself: boolean;
}

interface WorkingTimeStatisticsData {
	workingTimeSums?: {
		durationGross: number;
		duration: number;
		breakTime: number;
	};
}

export class WorkingTimeReportPage extends RecordingReport<ReportWorkingTime> {

	declare private setNoteDialog: SetNoteDialog;
	private excelWorkingTimeReportDialog: BootstrapModal.Modal;
	private excelWorkingTimeReportUserCombobox: Combobox<User>;
	private workingTimeDialog: WorkingTimeDialog;
	private workingTimeDetailsDialog: WorkingTimeDetailsDialog;
	private workingTimeRequestDialog: WorkingTimeRequestDialog;
	private workingTimeDateSpanDialog: WorkingTimeDateSpanDialog;

	constructor(public config: WorkingTimeReportConfig) {
		super(config, {
			formSelector: "#workingTimeFilterForm",
			roundingFiltersSelector: "#workingTimeRoundingFilters",
			filterButtonSelector: "#workingTimeFilterButton",
			bulkOperationDropDownSelector: "#workingTimeBulkOperationDropDown",
			bulkOperationButtonSelector: "#workingTimeBulkOperationButton",
			exportDropDownSelector: "#workingTimeExportDropDown",
			exportButtonSelector: "#workingTimeExportButton",
			roundingModeSelector: "#workingTimeRoundingMode",
			roundToMinutesSelector: "#workingTimeRoundToMinutes",
			groupDropdownSelector: "#workingTimeFilterGroupDropdown",
			userIdSelector: "#workingTimeFilterUserId",
			userFilterSelector: "#workingTimeFilterUserFilter",
			userCaretSelector: "#workingTimeFilterUserCaret",
			showInactiveSelector: "#workingTimeReportShowInactive",
			bulkOperationDeleteConfirmMessage: "workingtime.bulkdelete.message",
			bulkOperationDeleteUrl: "/reports/bulkDeleteWorkingTimes.json",
			bulkOperationSetStatusUrl: "/reports/updateWorkingTimeStatus.json",
			exportBaseUrl: "export/workingTimeReport.json",
			exportStatusBaseUrl: "export/status.json",
			accountExpiredTitle: "account.expired.workingtime.title",
			accountExpired: "account.expired.workingtime"
		});
	}

	protected override getReportingQueryParams() {
		return WorkingTimeReportingQueryParams;
	}

	protected override applyRecordTypeQueryParams(queryParams: typeof WorkingTimeReportingQueryParams, pendingHydrationRequests: PromiseLike<unknown>[]) {
		const appliedKeys = super.applyRecordTypeQueryParams(queryParams, pendingHydrationRequests);

		const workingTimeTypeId = queryParams.get("workingTimeTypeId");
		if (workingTimeTypeId) {
			$("#workingTimeFilterWorkingTimeTypeId").val(workingTimeTypeId);
			appliedKeys.push("workingTimeTypeId");
		}
		return appliedKeys;
	}

	protected init() {
		super.init();

		DOMUtil.fillWorkingTimeTypeReportSelect($("#workingTimeFilterWorkingTimeTypeId"), this.config.workingTimeTypes);

		if (globals.validateWorkingTime || globals.validateWorkingTimeOverlaps) {
			this.initValidationsListeners();
		}

		$(this.recordingTable).on(WorkingTimeTable.EVENT_DATA_CHANGED, (e, data: WorkingTimeStatisticsData) => {
			this.updateStatistics(data);
		});

		$("#createExcelWorkingTimeReportButton").click((e) => {
			e.preventDefault();
			this.showExcelWorkingTimeReportDialog();
		});

		$("#addWorkingTimeButton").click((e) => {
			e.preventDefault();
			this.showAddRecordDialog();
		}).toggleClass("disabled", getWorkingTimeTypesForAddWorkingTime(this.config.workingTimeTypes, true).length === 0);
		$("#addAbsenceButton").click((e) => {
			e.preventDefault();
			this.showAddAbsenceDialog();
		}).toggleClass("disabled", getWorkingTimeTypesForAddAbsence(this.config.workingTimeTypes, true).length === 0);

		$(this.recordingTable).on(WorkingTimeTable.EVENT_EDIT_DATE_SPAN, (e, workingTime: ReportWorkingTime) => {
			this.editWorkingTimeDateSpan(workingTime);
		});

		this.setNoteDialog = new SetNoteDialog({
			container: $("#bulkChangeNoteDialog"),
			apiUrl: "reports/bulkWorkingTimesSetNote.json"
		});
		$(this.setNoteDialog).on(SetNoteDialog.EVENT_NOTE_SET, (e, result: WorkingTimeBulkOperationResult) => {
			if (!result.allUpdated) {
				showRecordingBulkResult(result, "record.bulksetnote.");
			}
			this.reloadTable();
		});
	}

	protected getRecordById(recordId: number, link: JQuery) {
		return this.getWorkingTimeById(recordId, link);
	}

	protected buildFilterParams(): RequestParam[] {
		const params = $(this.baseConfig.formSelector).serializeArray();

		const workingTimeTypeOrCategory = $("#workingTimeFilterWorkingTimeTypeId").val() as string;
		if (workingTimeTypeOrCategory) {
			if (Object.values(WorkingTimeTypeCategory).includes(workingTimeTypeOrCategory as WorkingTimeTypeCategory)) {
				// add workingTimeTypeCategory param
				params.push({name: "workingTimeTypeCategory", value: workingTimeTypeOrCategory});
			} else {
				params.push({name: "workingTimeType", value: workingTimeTypeOrCategory});
			}
		}

		params.push({name: "startDate", value: getISODate(this.timeNavigationBar.getDateFrom())});
		params.push({name: "endDate", value: getISODate(this.timeNavigationBar.getDateTo())});
		return params.concat($(this.baseConfig.roundingFiltersSelector).serializeArray());
	}

	protected getWorkingTimeById(workingTimeRecordId: number, link: JQuery) {
		RecordingValidationUtil.setRecordingValidationLinkIconLoading(link);
		api("reports/getWorkingTime.json")
			.get({workingTimeId: workingTimeRecordId})
			.done((data) => {
				if (this.recordingTable.recordingChangeable(data)) {
					this.edit(data);
				} else {
					this.show(data);
				}
			})
			.always(() => RecordingValidationUtil.resetRecordingValidationLinkIconLoading(link));
	}

	protected createRecordingTable(): RecordingTable<ReportWorkingTime> {
		return new WorkingTimeTable({
			dataUrl: WorkingTimeTable.REPORT_WORKING_TIMES_URL,
			element: $("#workingTimeTable"),
			initialFilter: this.buildFilterParams(),
			allowQuickSwitch: false,
			allowResume: false,
			allowSelect: true,
			customFieldDefinitions: this.config.customFieldDefinitions,
			statusNumbers: this.config.statusNumbers,
			allowedUserIds: this.config.allowedUserIds,
			showUser: true,
			orderable: true,
			clampNote: false,
			allowExpand: false,
			pageLength: Constants.REPORT_TABLES_PAGE_LENGTH,
			paging: true,
			excludeElementsDblclickEvent: [RecordingReport.EXCLUDE_ELEMENTS_DBLCLICK_EVENT]
		});
	}

	protected setUsers(users: User[]) {
		super.setUsers(users);
		if (this.workingTimeDialog != null) {
			this.workingTimeDialog.config.users = users;
		}
	}

	protected show(workingTime: ReportWorkingTime) {
		if (workingTime.hasWorkingTimeRequest) {
			api("requests/findWorkingTimeRequestByWorkingTimeId.json")
				.get({workingTimeId: workingTime.id})
				.done((data) => this.getWorkingTimeRequestDetailsDialog().show(data))
				// If not found, show working time details dialog
				.fail(() => this.getWorkingTimeDetailsDialog().show(workingTime));
		} else if (workingTime.workingTimeDateSpanId !== null) {
			api("workingTimeDateSpans/findWorkingTimeDateSpanWithValidationsByWorkingTimeId.json")
				.get({workingTimeId: workingTime.id})
				.done((response) => this.getWorkingTimeDateSpanDetailsDialog().show(response.workingTimeDateSpan, buildValidationsListItem(workingTime.id, response.validations, true)))
				// If not found, show working time details dialog
				.fail(() => this.workingTimeDetailsDialog.show(workingTime));
		} else {
			this.getWorkingTimeDetailsDialog().show(workingTime);
		}
	}

	private editWorkingTimeDateSpan(workingTime: ReportWorkingTime) {
		api("workingTimeDateSpans/findWorkingTimeDateSpanWithValidationsByWorkingTimeId.json")
			.get({workingTimeId: workingTime.id})
			.done((response) => this.getWorkingTimeDateSpanDetailsDialog().edit(response.workingTimeDateSpan, buildValidationsListItem(workingTime.id, response.validations, true)))
			// If not found, show the working time details
			.fail(() => this.workingTimeDetailsDialog.show(workingTime));
	}

	protected edit(recording: ReportWorkingTime) {
		this.getWorkingTimeDialog().edit(recording);
	}

	protected duplicate(recording: ReportWorkingTime) {
		this.getWorkingTimeDialog().duplicate(recording);
	}

	private updateStatistics(data: WorkingTimeStatisticsData) {
		const workingTimeSums = data.workingTimeSums;
		let durationGrossSum = 0;
		let durationNetSum = 0;
		let durationBreakTimeSum = 0;
		if (workingTimeSums) {
			durationGrossSum = workingTimeSums.durationGross;
			durationNetSum = workingTimeSums.duration;
			durationBreakTimeSum = workingTimeSums.breakTime * 60 * 1000;
		}
		$("#workingTimeDurationGrossSum").html(formatDuration(durationGrossSum));
		$("#workingTimeDurationNetSum").html(formatDuration(durationNetSum));
		$("#workingTimeBreakTimeSum").html(formatDuration(durationBreakTimeSum));
	}

	private createExcelWorkingTimeReportDialog() {
		this.excelWorkingTimeReportDialog = new BootstrapModal.Modal({
			title: "excelWorkingTimeReport.header",
			message: $("#excelWorkingTimeReportDialog"),
			buttons: [{
				text: "dialog.create.report",
				action: () => {
					this.createExcelWorkingTimeReport();
				},
				css: "btn-primary"
			}, {
				text: "dialog.cancel"
			}]
		});
		this.excelWorkingTimeReportUserCombobox = new Combobox({
			items: this.config.users,
			formatter: (user) => {
				return user.fullname;
			},
			idElement: $("#excelWorkingTimeReportUserId"),
			filterElement: $("#excelWorkingTimeReportUserFilter"),
			caretElement: $("#excelWorkingTimeReportUserCaret"),
			emptyMessage: messages["excelWorkingTimeReport.all"]
		});
	}

	private showExcelWorkingTimeReportDialog() {
		if (this.excelWorkingTimeReportDialog == null) {
			this.createExcelWorkingTimeReportDialog();
		}
		if (this.users != null) {
			this.excelWorkingTimeReportUserCombobox.updateItems(this.users);
		}
		this.excelWorkingTimeReportDialog.show();
	}

	private createExcelWorkingTimeReport() {
		const year = $("#excelWorkingTimeReportYear").val() as string;
		if (!year || year.length === 0 || isNaN(Number(year))) {
			BootstrapModal.error({
				message: "dialog.yearisnotanumber"
			});
			return;
		}

		const month = $("#excelWorkingTimeReportMonth").val() as string;
		const user = $("#excelWorkingTimeReportUserId").val() as string;

		api("reports/createExcelWorkingTimeReport.json")
			.post({year, month, user})
			.done(data => {
				if (data.exportJobId) {
					new DownloadStatus({
						exportJobId: data.exportJobId,
						exportType: ExportType.Excel,
						allowCancellation: true
					});
				}
			});
	}

	private createWorkingTimeDetailsDialog(): WorkingTimeDetailsDialog {
		return new WorkingTimeDetailsDialog({
			title: messages["workingTime.details.header"],
			dialogId: "#workingTimeDetailsDialog",
			customFieldDefinitions: this.config.customFieldDefinitions,
			editRecord: (recording: ReportWorkingTime) => this.edit(recording)
		});
	}

	private getWorkingTimeDetailsDialog(): WorkingTimeDetailsDialog {
		if (this.workingTimeDetailsDialog == null) {
			this.workingTimeDetailsDialog = this.createWorkingTimeDetailsDialog();
		}
		return this.workingTimeDetailsDialog;
	}

	private getWorkingTimeRequestDetailsDialog(): WorkingTimeRequestDialog {
		if (this.workingTimeRequestDialog == null) {
			this.workingTimeRequestDialog = this.createWorkingTimeRequestDialog();
		}
		return this.workingTimeRequestDialog;
	}

	private createWorkingTimeDialog(): WorkingTimeDialog {
		const dialog = new WorkingTimeDialog({
			dialogId: "#workingTimeDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			users: this.users,
			userSelectionEnabled: globals.isGroupLeader,
			customFieldDefinitions: this.config.customFieldDefinitions,
			defaultStatusNumber: this.config.defaultStatusNumber,
			allowedUserIds: this.config.allowedUserIds,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.dateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.dateTimeChangeableByUserDayRangeMode
		});
		$(dialog).on(WorkingTimeDialog.EVENT_RECORD_EDITED, () => {
			this.reloadTable();
		}).on(WorkingTimeDialog.EVENT_RECORD_CREATED, () => {
			this.filterTable();
		}).on(WorkingTimeDialog.EVENT_RECORD_DELETED, () => {
			this.filterTable();
		});
		return dialog;
	}

	private createWorkingTimeRequestDialog(): WorkingTimeRequestDialog {
		const dialog = new WorkingTimeRequestDialog({
			dialogId: "#workingTimeRequestDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			userSelectionEnabled: globals.isGroupLeader,
			customFieldDefinitions: this.config.customFieldDefinitions
		});
		$(dialog).on(WorkingTimeRequestDialog.EVENT_REQUEST_EDITED, () => {
			this.reloadTable();
		}).on(WorkingTimeRequestDialog.EVENT_REQUEST_STATUS_CHANGED, () => {
			this.filterTable();
		}).on(WorkingTimeRequestDialog.EVENT_REQUEST_DELETED, () => {
			this.filterTable();
		});
		return dialog;
	}

	private getWorkingTimeDateSpanDetailsDialog(): WorkingTimeDateSpanDialog {
		if (this.workingTimeDateSpanDialog == null) {
			this.workingTimeDateSpanDialog = this.createWorkingTimeDateSpanDialog();
		}
		return this.workingTimeDateSpanDialog;
	}

	private createWorkingTimeDateSpanDialog(): WorkingTimeDateSpanDialog {
		const dialog = new WorkingTimeDateSpanDialog({
			dialogId: "#workingTimeDateSpanDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			userSelectionEnabled: globals.isGroupLeader
		});
		$(dialog).on(WorkingTimeDateSpanDialog.EVENT_DATE_SPAN_EDITED, () => {
			this.reloadTable();
		}).on(WorkingTimeDateSpanDialog.EVENT_DATE_SPAN_DELETED, () => {
			this.reloadTable();
		});
		return dialog;
	}

	private getWorkingTimeDialog(): WorkingTimeDialog {
		if (this.workingTimeDialog == null) {
			this.workingTimeDialog = this.createWorkingTimeDialog();
		}
		return this.workingTimeDialog;
	}

	protected getSecurityStatus() {
		return SecurityStatus.isAddWorkingTimeAllowed();
	}

	protected createAddRecordDialog(preSelectUser: User) {
		this.getWorkingTimeDialog().create(preSelectUser ? {userId: preSelectUser?.id} : null, false,
			getWorkingTimeTypesForAddWorkingTime(this.config.workingTimeTypes, true));
	}

	private showAddAbsenceDialog() {
		if (!this.getSecurityStatus()) {
			BootstrapModal.info({
				title: this.baseConfig.accountExpiredTitle,
				message: this.baseConfig.accountExpired,
				escapeText: false
			});
			return;
		}
		const userIdInput = $(this.baseConfig.userIdSelector);
		const preSelectUser: User | null = userIdInput.length === 1 ? this.userCombobox.getSelectedItem() : null;
		this.getWorkingTimeDialog().create(preSelectUser ? {userId: preSelectUser.id} : null, false, getWorkingTimeTypesForAddAbsence(this.config.workingTimeTypes, true));
	}

	protected showSetNoteDialog() {
		this.setNoteDialog.show(this.buildBulkOperationParams(), this.recordingTable.bulkSelector.getSelectedEntriesCount());
	}

	protected createBulkMenu() {
		const menu = super.createBulkMenu();

		// add set note menu item in new section
		menu.addDivider();
		menu.addMenuEntry({
			label: messages["report.setNote"],
			action: () => this.showSetNoteDialog(),
			show: true,
			active: true
		});

		// add delete menu item last
		menu.addDivider();
		menu.addMenuEntry({
			label: messages["report.delete"],
			action: () => this.bulkOperationDelete(),
			show: true,
			active: true
		});
		return menu;
	}
}
